import { singleSelectionLabel } from '../../../utils/filterLabels.js';
import { hidesPlaceholders, readSizeGranularity } from '../../utils/analyticsHelpers.js';

/** @typedef {{ iso_week: number, iso_year: number, week_label?: string, week_start?: string }} IsoWeekOption */

export const weekKey = (w) => (w ? `${w.iso_year}-${w.iso_week}` : '');

export const sortWeeksNewestFirst = (weeks) =>
  [...(weeks || [])].sort((a, b) => {
    if (a.iso_year !== b.iso_year) return b.iso_year - a.iso_year;
    return b.iso_week - a.iso_week;
  });

/** Default: last N ISO weeks from available list (newest first). */
export const lastNIsoWeeks = (weeks, n = 4) => sortWeeksNewestFirst(weeks).slice(0, n);

/** Fields shown in Details column (cascade order). Excludes week, origin, terminal — already table columns. */
export const DETAIL_FIELD_DEFS = [
  { key: 'type' },
  { key: 'variety' },
  { key: 'item_size' },
  { key: 'grade' },
  { key: 'package', altKey: 'sku' },
  { key: 'storage' },
  { key: 'condition' },
  { key: 'environment' },
  { key: 'quality' },
  { key: 'appearance' },
  { key: 'properties' },
];

/**
 * Details fields for a cucumber row: Size Group and Size (granularity) replace
 * the raw `item_size`, so a Field bushel row never shows its printed count.
 */
export const CUCUMBER_DETAIL_FIELD_DEFS = [
  { key: 'type' },
  { key: 'variety' },
  { key: 'size_group' },
  { key: 'package', altKey: 'sku' },
  { key: 'size_granularity' },
  { key: 'grade' },
  { key: 'storage' },
  { key: 'condition' },
  { key: 'environment' },
  { key: 'quality' },
  { key: 'appearance' },
  { key: 'properties' },
];

/** Detail field list for `commodity`; every non-gated commodity keeps {@link DETAIL_FIELD_DEFS}. */
export const detailFieldDefs = (commodity) =>
  (hidesPlaceholders(commodity) ? CUCUMBER_DETAIL_FIELD_DEFS : DETAIL_FIELD_DEFS);

const DETAIL_IGNORE = new Set(['N/A', 'Not Classified', 'n/a', '']);

export function rowDetailValue(row, def) {
  if (def.key === 'package') return row?.package || row?.sku || '';
  if (def.key === 'size_granularity') return readSizeGranularity(row) || '';
  return row?.[def.key] || '';
}

export function isDetailValue(value) {
  if (value == null) return false;
  const s = String(value).trim();
  return s.length > 0 && !DETAIL_IGNORE.has(s);
}

/** Values only, cascade order, skipping N/A and columns already in the table. */
export function formatRowDetails(row, commodity) {
  const parts = [];
  for (const def of detailFieldDefs(commodity)) {
    const v = rowDetailValue(row, def);
    if (isDetailValue(v)) parts.push(String(v).trim());
  }
  return parts.length ? parts.join(' · ') : '—';
}

/** Multi-select: empty selection = no rows; full selection = no filter. */
export function applyArrayFilter(rows, field, selected, allOptions, getValue) {
  if (!allOptions?.length) return rows;
  if (!selected?.length) return [];
  if (selected.length >= allOptions.length) return rows;
  const pick = getValue || ((r) => r[field]);
  return rows.filter((r) => {
    const v = pick(r);
    return v && selected.includes(v);
  });
}

export function applyPackageFilter(rows, selected, allPackages) {
  if (!allPackages?.length) return rows;
  if (!selected?.length) return [];
  if (selected.length >= allPackages.length) return rows;
  return rows.filter((r) => {
    const p = r.package || r.sku || '';
    return p && selected.includes(p);
  });
}

export function multiSelectDropdownLabel(
  selected,
  options,
  { allLabel, nSelectedLabel, emptyLabel, getOptionLabel }
) {
  const opts = options || [];
  const sel = Array.isArray(selected) ? selected : [];
  // One selected value is shown by name rather than summarised, so a filter
  // narrowed to a single option says which one it is.
  const onlySelection = singleSelectionLabel(sel, opts, getOptionLabel);
  if (onlySelection) return onlySelection;
  const activeCount = sel.filter((v) => opts.some((o) => String(o) === String(v))).length;
  if (opts.length > 0 && activeCount === opts.length) return allLabel;
  if (activeCount > 0) return nSelectedLabel(activeCount);
  return emptyLabel;
}
